import { SafeSurveyModel } from '@/safeRendering';
import { markRaw } from 'vue';
import {PlainLightPanelless} from "survey-core/themes";
import {LicenseApplicationForm} from "@/models/LicenseApplicationForm";
import { setSurveyLicenseContext } from '@/surveyLicenseContext';

async function blobUrlToDataUrl(blobUrl) {
  const response = await fetch(blobUrl);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = reject;
    fr.onload = () => resolve(fr.result);
    fr.readAsDataURL(blob);
  });
}

async function convertBlobUrls(value) {
  if (typeof(value) === 'string') {
    if (value.startsWith('blob:')) {
      return blobUrlToDataUrl(value);
    }
  } else if (value instanceof Array) {
    return Promise.all(value.map(convertBlobUrls));
  } else if (value && typeof value === 'object' && value.content) {
    value.content = await convertBlobUrls(value.content);
  }
  return value;
}

export default class {

  /**
   *
   * @param {string} surveyJson
   * @param {boolean} editable
   * @param {boolean} disableFileUploads
   * @param {(name:string,value:any) => void} onValueChanged
   * @return {SurveyModel}
   */
  static createFromJson(surveyJson, {
    editable = false,
    disableFileUploads = false,
    onValueChanged,
    license = null
  } = {}) {

    // Each view owns its settings: Review must not make the editor's signatures
    // or uploads read-only by mutating the shared form configuration.
    const json = typeof surveyJson === 'string'
      ? JSON.parse(surveyJson)
      : JSON.parse(JSON.stringify(surveyJson ?? {}));

    // If not editable, file uploads must be disabled
    if (!editable) {
      disableFileUploads = true;
    }

    // Put constraints around file upload fields: store as text, and limit size to 5MB
    const fixFileElements = parent => {
      if (!parent || typeof parent !== 'object') return;
      ['pages', 'elements', 'templateElements'].forEach(collectionName => {
        (parent[collectionName] || []).forEach(element => {
          if (element.type === 'file') {
            element.waitForUpload = disableFileUploads;
            element.storeDataAsText = !disableFileUploads;
            element.maxSize = 10 * 1024 * 1024;
            element.readOnly = disableFileUploads;
          } else if (element.type === 'signaturepad' && disableFileUploads) {
            element.readOnly = true;
          }
          fixFileElements(element);
        });
      });
    };
    fixFileElements(json);

    // SurveyJS manages its own Vue refs. Vue's deep proxy conflicts with those
    // refs during validation, causing recursive updates when questions render.
    const model = markRaw(new SafeSurveyModel(json));
    model.applyTheme(PlainLightPanelless);
    if (!editable) {
      model.mode = 'display';
    }
    model.showCompleteButton = false;
    model.showNavigationButtons = false;
    model.widthMode = 'responsive';

    // Keep original references in SurveyJS. The file item requests temporary access when rendered.
    setSurveyLicenseContext(model, license);
    model.onDownloadFile.add((_, options) => {
      if (typeof options.content !== 'string') {
        options.callback('error', 'Attachment is unavailable.');
        return;
      }

      options.callback('success', options.content);
    });

    // When uploading a new file, convert it to a data URL
    model.onUploadFiles.add(async (_, options) => {
      try {
        const files = await Promise.all(options.files.map(async file => ({
          file: {
            name: file.name,
            type: file.type,
            size: file.size
          },
          content: URL.createObjectURL(file)
        })));
        options.callback('success', files);
      } catch (error) {
        console.error(`Could not convert files to data URL's`, error);
        options.callback('error', error.message);
      }
    });

    // Hook value-change handler
    if (onValueChanged) {
      model.onValueChanged.add(async (_, options) => {
        let value = await convertBlobUrls(options.value);
        onValueChanged(options.name, value);
      });
    }

    return model;
  }

  /**
   * @param {LicenseApplicationForm} form
   * @param {Boolean} editable
   */
  static createFromApplicationForm(form, { editable, disableFileUploads, onValueChanged, license } = {}) {
    return this.createFromJson(form.surveyJson, {
      editable,
      disableFileUploads,
      onValueChanged,
      license
    });
  }

  /**
   * @param {License} license
   */
  static createFromLicense(license) {
    if (!license?.city) {
      return null;
    }
    let survey = this.createFromApplicationForm(new LicenseApplicationForm(license.city), { license });
    survey.data = license.customFieldData;
    return survey;
  }

  /**
   * @param {LicenseEditorModel} model
   * @param {boolean} editable
   * @param {function} onUpdated
   */
  static createFromLicenseEditor(model, { editable = true, disableFileUploads = false, onUpdated }) {
    let survey = this.createFromApplicationForm(model.form, {
      editable,
      disableFileUploads,
      license: model.id ? model : null,
      onValueChanged: editable ? (name, value) => {
        model.setCustomFieldValue(name, value);
        onUpdated && onUpdated();
      } : undefined
    });
    const answers = model.customFieldData;
    survey.mergeData(answers);

    // SurveyJS applies defaults during construction, before value-change handlers
    // are installed. Persist those initial answers so Finish sends the same values
    // that made the displayed form valid, without overwriting existing answers.
    if (editable && model.isNew) {
      for (const [name, value] of Object.entries(survey.data)) {
        const question = survey.getQuestionByName(name);
        if (!Object.hasOwn(answers, name) && question && !question.isReadOnly &&
          !['expression', 'html', 'image'].includes(question.getType())) {
          model.setCustomFieldValue(name, value);
        }
      }
    }
    return survey;
  }
}
